import styled from "styled-components";
import {useAppDispatch} from "../../../../hooks";
import {updateCurrentRecord} from "../../../../features/tableSlice";
import {apiFetch, confirmAction} from "../../../util";
import React, {useCallback, useEffect, useState} from "react";
import Loader from "../../../Loader";
import {Field, Primitive, PrimitiveObject} from "../../../../Types";
import EditColumnRow, {getDefaultValueForType, stringToPrimitiveValue} from "./EditColumnRow";
import {useApiFetch} from "../../../hooks/useApiFetch";
import {SqlQuery} from "../../../schemaManage/back/BackTableFieldViewTable";
import {toast} from "react-toastify";
import {ColumnInfo, EditValueI} from "./EditTableRecord";


type PropsI = {
    pkValue: string | number | undefined,
    backendUrl: string,
    fields: Field[];
    project: string,
    tableKey: string,
    closeEdit: () => void
};

export default function AddTableRecord({pkValue, backendUrl, project, tableKey, fields, closeEdit}: PropsI) {
    const [schema, setSchema] = useState<Record<string, ColumnInfo>>();
    const [currentValue, setCurrentValue] = useState<PrimitiveObject>();
    const [newValue, setNewValue] = useState<Record<string, EditValueI>>();
    const [isUpdating, setIsUpdating] = useState<boolean>(false);

    const {fetchResult, loading, success, error} = useApiFetch<{
        data: {
            schema: ColumnInfo[];
            value: PrimitiveObject;
        }
    }>(`${backendUrl}/astra/getRawEditSchema.php?project=${project}&table=${tableKey}${pkValue !== undefined ? "&pk=" + pkValue : ''}`);

    useEffect(() => {
        if (!fetchResult || !success) return;
        const schemaMap = {} as Record<string, ColumnInfo>;
        const curr = {} as PrimitiveObject;
        const newVal = {} as Record<string, EditValueI>;
        const fieldsMap = new Map(fields.map(f => [f.key, f]));

        for (const col of fetchResult.data.schema) {
            schemaMap[col.column_name] = col;

            const field = fieldsMap.get(col.column_name); // might be undefined

            let def: Primitive = null;
            let format: EditValueI['format'] = "const"
            if (fetchResult.data.value) {
                def = fetchResult.data.value[col.column_name]
                format = def === null ? "null" : "const"
            } else {
                if (col.column_default) {
                    def = col.expr_type === "FUNCEXPR"
                        ? col.column_default
                        : stringToPrimitiveValue(col.column_default, col.php_type);
                } else if (col.not_null && field) {
                    def = getDefaultValueForType(field.type, col, field);
                }
                format = col.column_default
                    ? (col.expr_type === "FUNCEXPR" ? "expression" : "const")
                    : (col.not_null ? "const" : "null")
            }

            curr[col.column_name] = def;
            newVal[col.column_name] = {
                value: def,
                format: format,
                isEdited: false,
                invalid: false
            };
        }

        setSchema(schemaMap);
        setCurrentValue(curr);
        setNewValue(newVal);
    }, [fetchResult, fields, success]);

    const addEntry = async () => await confirmAction({
        message: "Додати запис?",
        onConfirm: async () => {
            const invalid = Object.entries(newValue!).some(x => x[1].invalid);
            if (invalid) {
                toast.error("Присутні неправильні поля!")
                return
            }
            setIsUpdating(true)
            const {success, body} = await apiFetch(`${backendUrl}/achillea/rawAddRecord.php`, {
                method: "POST",
                body: JSON.stringify({
                    project: project,
                    table: tableKey,
                    record: Object.fromEntries(
                        Object.entries(newValue!)
                            .filter(([key, val]) => !schema![key].is_primary_key)
                            .map(([key, val]) => [key, {
                                format: val.format,
                                value: val.value,
                                type: schema![key].php_type
                            }])
                    )
                })
            });
            setIsUpdating(false)
            if (success) {
                toast.success(body.message);
                closeEdit()
            }
        }
    });

    const setValue = useCallback((column: string, curr: EditValueI) => {
        setNewValue(prev => ({...prev, [column]: curr}));
    }, []);

    return (
        <Container>
            {loading && <Loader/>}
            {error && <p style={{color: "red"}}>Error: {error}</p>}
            <p>{pkValue === undefined ? "Додати новий запис" : "Додати новий запис на прикладі існуючого"}</p>
            {schema && currentValue && newValue && (
                <table>
                    <thead>
                    <tr className="tr-head">
                        <th>Поле</th>
                        <th>Формат</th>
                        <th>Значення</th>
                    </tr>
                    </thead>
                    <tbody>
                    {Object.values(schema).map((s,i) => (
                        <EditColumnRow
                            key={i}
                            setValue={setValue}
                            info={s}
                            schema={fields.find(x => x.key === s.column_name)}
                            currentValue={currentValue[s.column_name]}
                            value={newValue[s.column_name]}
                            isNew
                        />
                    ))}
                    </tbody>
                    <tfoot>
                    <tr className="tr-head">
                        <td colSpan={3}>
                            Усього полів: {Object.values(currentValue).length}
                        </td>
                    </tr>
                    </tfoot>
                </table>
            )}
            <div className="summary">
                <div className="new-rec">
                    <table>
                        <thead>
                        <tr>
                            <td colSpan={2}>Новий запис:</td>
                        </tr>
                        </thead>
                        <tbody>
                        {newValue && Object.entries(newValue)
                            .filter(([key, val]) => !schema![key].is_primary_key)
                            .map(([key, val]) => <tr key={key}>
                                <td className="key">{key}:</td>
                                <td className="val">{
                                    val.format === "expression" ?
                                        <SqlQuery sqlQuery={val.value as string}/> :
                                        <NewVal val={val.value}/>
                                }</td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>
                <div className="edit-controls">
                    <button className={'btn-cmn'} onClick={addEntry} disabled={isUpdating}>
                        Створити
                    </button>
                </div>
            </div>
        </Container>
    );
}

const NewVal = ({val}: { val: Primitive }) => {

    if (val === null)
        return <i className={"null-value"}>NULL</i>;

    if (val === '')
        return <i>*порожній рядок*</i>

    return <>{String(val)}</>
}


const Container = styled.div`
    background-color: var(--table-row-odd-bg-color);
    padding: .5rem;
    border-radius: .5rem;
    width: 100%;
    height: 100%;
    display: flex;
    flex-flow: column;
    gap: .5rem;

    & .summary {
        display: flex;
        gap: .5rem;
        flex-flow: column;

        & .edit-controls {
            padding-bottom: .5rem;

            & button {
                width: 100%;
            }
        }

        & .new-rec {
            tbody, thead {
                border-top: 2px solid var(--table-border-color);
                border-bottom: 2px solid var(--table-border-color);
            }

            .val {
                width: 100%;
            }
        }

        & .new-vals {
            display: flex;
            gap: .5rem;
            flex-flow: column;

            & .inv {
                border-color: red;
            }

            & .edit-val {
                display: flex;
                gap: .5rem;
                flex-flow: wrap;
                align-items: center;
                border-radius: .2rem;
                padding: .2rem;
                border: 2px solid var(--table-border-color)
            }
        }
    }


    & .tr-head {
        border-top: 2px solid var(--table-border-color);
        border-bottom: 2px solid var(--table-border-color);
        background: var(--table-header-bg-color);
    }

    & td, th {
        border-left: 2px solid var(--table-border-color);
        border-right: 2px solid var(--table-border-color);
    }


    .controls {
        display: flex;
        justify-content: end;
        gap: .5rem;
    }

    .check {
        display: flex;
        margin-top: auto;
        gap: .5rem;
    }

    & .entries {
        display: flex;
        flex-flow: column;
        gap: .5rem;
        justify-content: center;
    }

    .loader {
        width: 100%;
        height: 500px;
    }
`



